import React, { ReactElement, useState, useEffect, useContext } from 'react';
import { 
    Box, 
    Typography, 
    TableCell, 
    TableRow, 
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Skeleton
} from '@mui/material';
import { AdminLayout } from './shared/AdminLayout';
import { AdminTable, AdminTableColumn } from './shared/AdminTable';
import { AdminPagination } from './shared/AdminPagination';
import { AdminEmptyState } from './shared/AdminEmptyState';
import { UserDetailModal } from './UserDetailModal';
import { 
    Person as PersonIcon,
    Business as BusinessIcon,
    Schedule as ScheduleIcon,
    TrendingUp as TrendingUpIcon,
    AccessTime as AccessTimeIcon,
    CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { UserContext } from '../../contexts/UserContext';
import { HttpClient } from '../../api/HttpClient';
import { PlanType, PlanStatus, PaginatedUsersResponse, AdminUser } from '../../api/models/User';

export function AdminUsers(): ReactElement {
    const { authToken } = useContext(UserContext);
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [planFilter, setPlanFilter] = useState<PlanType | 'all'>('all');
    const [planStatusFilter, setPlanStatusFilter] = useState<PlanStatus | 'all'>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sortBy, setSortBy] = useState<string>('email');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchUsers = async (page: number = currentPage, size: number = pageSize, sortField: string = sortBy, sortDirection: 'asc' | 'desc' = sortOrder) => {
        if (!authToken) return;
        
        try {
            setLoading(true);
            const httpClient = new HttpClient(authToken);
            const planTypeParam = planFilter === 'all' ? undefined : planFilter;
            const planStatusParam = planStatusFilter === 'all' ? undefined : planStatusFilter;
            
            const params = new URLSearchParams();
            if (planTypeParam !== undefined) params.append('planType', planTypeParam.toString());
            if (planStatusParam !== undefined) params.append('planStatus', planStatusParam.toString());
            params.append('page', page.toString());
            params.append('pageSize', size.toString());
            params.append('sortBy', sortField);
            params.append('sortOrder', sortDirection);
            
            const queryString = params.toString();
            const response = await httpClient.get<PaginatedUsersResponse>(`/admin/users?${queryString}`);
            
            setUsers(response.users);
            setTotalCount(response.totalCount);
            setTotalPages(response.totalPages);
            setCurrentPage(response.page);
            setError(null);
        } catch (err: unknown) {
            if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 403) {
                setError('Access denied. You must be an administrator to view this page.');
            } else {
                setError('Failed to fetch users');
            }
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1); // Reset to first page when filters change
        fetchUsers(1);
    }, [authToken, planFilter, planStatusFilter, pageSize, sortBy, sortOrder]);

    const getPlanTypeColor = (planType: PlanType) => {
        switch (planType) {
            case PlanType.Premium:
                return 'success';
            case PlanType.Trial:
                return 'warning';
            case PlanType.Free:
                return 'info';
            default:
                return 'default';
        }
    };

    const getPlanStatusColor = (planStatus: PlanStatus) => {
        switch (planStatus) {
            case PlanStatus.Active:
                return 'success';
            case PlanStatus.Canceled:
                return 'error';
            case PlanStatus.Expired:
                return 'warning';
            default:
                return 'default';
        }
    };

    const handleSort = (field: string) => {
        if (sortBy === field) {
            // Toggle sort order if same field
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field and default to ascending
            setSortBy(field);
            setSortOrder('asc');
        }
        setCurrentPage(1); // Reset to first page when sorting changes
    };

    const handleUserClick = (user: AdminUser) => {
        setSelectedUserId(user.id);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedUserId(null);
    };

    const getSortIcon = (field: string) => {
        if (sortBy !== field) return '↕️';
        return sortOrder === 'asc' ? '↑' : '↓';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };


    const filtersComponent = (
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Plan Type</InputLabel>
                <Select
                    value={planFilter}
                    label="Plan Type"
                    onChange={(e) => setPlanFilter(e.target.value as PlanType | 'all')}
                >
                    <MenuItem value="all">All Plan Types</MenuItem>
                    <MenuItem value={PlanType.Premium}>Premium</MenuItem>
                    <MenuItem value={PlanType.Trial}>Trial</MenuItem>
                    <MenuItem value={PlanType.Free}>Free</MenuItem>
                </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Plan Status</InputLabel>
                <Select
                    value={planStatusFilter}
                    label="Plan Status"
                    onChange={(e) => setPlanStatusFilter(e.target.value as PlanStatus | 'all')}
                >
                    <MenuItem value="all">All Plan Statuses</MenuItem>
                    <MenuItem value={PlanStatus.Active}>Active</MenuItem>
                    <MenuItem value={PlanStatus.Canceled}>Canceled</MenuItem>
                    <MenuItem value={PlanStatus.Expired}>Expired</MenuItem>
                </Select>
            </FormControl>
        </Stack>
    );

    const columns: AdminTableColumn[] = [
        {
            key: 'email',
            label: 'Email',
            width: '25%',
            minWidth: 200,
            sortable: true,
            icon: <PersonIcon fontSize="small" color="action" />,
            sortIcon: getSortIcon('email'),
            onClick: () => handleSort('email')
        },
        {
            key: 'plantype',
            label: 'Plan Type',
            width: '12%',
            minWidth: 100,
            sortable: true,
            icon: <BusinessIcon fontSize="small" color="action" />,
            sortIcon: getSortIcon('plantype'),
            onClick: () => handleSort('plantype')
        },
        {
            key: 'planstatus',
            label: 'Plan Status',
            width: '12%',
            minWidth: 100,
            sortable: true,
            icon: <ScheduleIcon fontSize="small" color="action" />,
            sortIcon: getSortIcon('planstatus'),
            onClick: () => handleSort('planstatus')
        },
        {
            key: 'planstartdate',
            label: 'Plan Start',
            width: '15%',
            minWidth: 120,
            sortable: true,
            icon: <CalendarTodayIcon fontSize="small" color="action" />,
            sortIcon: getSortIcon('planstartdate'),
            onClick: () => handleSort('planstartdate')
        },
        {
            key: 'lastsignin',
            label: 'Last Sign In',
            width: '13%',
            minWidth: 120,
            sortable: true,
            icon: <AccessTimeIcon fontSize="small" color="action" />,
            sortIcon: getSortIcon('lastsignin'),
            onClick: () => handleSort('lastsignin')
        },
        {
            key: 'activerooms',
            label: 'Active Rooms',
            width: '10%',
            minWidth: 100,
            sortable: true,
            icon: <TrendingUpIcon fontSize="small" color="action" />,
            sortIcon: getSortIcon('activerooms'),
            onClick: () => handleSort('activerooms')
        }
    ];

    const renderRow = (user: AdminUser) => (
        <TableRow 
            key={user.id}
            onClick={() => handleUserClick(user)}
            sx={{ 
                '&:hover': { 
                    bgcolor: 'action.hover',
                    transform: 'translateY(-1px)',
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? 3 : 2,
                    '& .MuiTableCell-root': {
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider'
                    }
                },
                transition: 'all 0.2s ease-in-out',
                cursor: 'pointer',
                '&:nth-of-type(even)': {
                    bgcolor: 'action.selected'
                },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    color: 'text.primary'
                }
            }}
        >
            <TableCell sx={{ py: 2, width: '25%', minWidth: 200 }}>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                    title={user.email}
                >
                    {user.email}
                </Typography>
            </TableCell>
            <TableCell sx={{ py: 2, width: '12%', minWidth: 100 }}>
                <Chip 
                    label={PlanType[user.planType]} 
                    color={getPlanTypeColor(user.planType)}
                    size="small"
                    sx={{ 
                        fontWeight: 500,
                        textTransform: 'capitalize',
                        '& .MuiChip-label': {
                            color: 'inherit'
                        }
                    }}
                />
            </TableCell>
            <TableCell sx={{ py: 2, width: '12%', minWidth: 100 }}>
                <Chip 
                    label={PlanStatus[user.planStatus]} 
                    color={getPlanStatusColor(user.planStatus)}
                    size="small"
                    sx={{ 
                        fontWeight: 500,
                        textTransform: 'capitalize',
                        '& .MuiChip-label': {
                            color: 'inherit'
                        }
                    }}
                />
            </TableCell>
            <TableCell sx={{ py: 2, width: '15%', minWidth: 120 }}>
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                    title={formatDate(user.planStartDate)}
                >
                    {formatDate(user.planStartDate)}
                </Typography>
            </TableCell>
            <TableCell sx={{ py: 2, width: '13%', minWidth: 120 }}>
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                    title={formatDate(user.lastSignInAt)}
                >
                    {formatDate(user.lastSignInAt)}
                </Typography>
            </TableCell>
            <TableCell sx={{ py: 2, width: '10%', minWidth: 100 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: user.activeRoomsCount > 0 ? 600 : 400,
                            color: user.activeRoomsCount > 0 ? 'success.main' : 'text.secondary'
                        }}
                    >
                        {user.activeRoomsCount}
                    </Typography>
                    {user.activeRoomsCount > 0 && (
                        <TrendingUpIcon 
                            fontSize="small" 
                            sx={{ 
                                color: 'success.main',
                                filter: 'drop-shadow(0 0 2px currentColor)'
                            }} 
                        />
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );

    const resultsSummary = (
        <Box sx={{ 
            mb: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {loading ? (
                    <Skeleton variant="text" width={200} height={20} />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Showing {users.length} of {totalCount} user{totalCount !== 1 ? 's' : ''} 
                        (Page {currentPage} of {totalPages})
                    </Typography>
                )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                    <InputLabel>Per Page</InputLabel>
                    <Select
                        value={pageSize}
                        label="Per Page"
                        onChange={(e) => {
                            const newPageSize = Number(e.target.value);
                            setPageSize(newPageSize);
                            setCurrentPage(1);
                            fetchUsers(1, newPageSize);
                        }}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );

    const paginationComponent = (
        <AdminPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            loading={loading}
            onChange={(page) => {
                setCurrentPage(page);
                fetchUsers(page);
            }}
        />
    );

    const emptyStateComponent = (
        <AdminEmptyState
            icon={<PersonIcon />}
            title="No users found"
            description="Try adjusting your filters to see more results"
        />
    );

    return (
        <>
            <AdminLayout
                title="User Management"
                description="Manage and monitor user accounts, subscription plans, and activity"
                filters={filtersComponent}
                resultsSummary={resultsSummary}
                table={
                    <AdminTable
                        columns={columns}
                        data={users}
                        loading={loading}
                        renderRow={renderRow}
                        emptyState={emptyStateComponent}
                    />
                }
                pagination={paginationComponent}
                error={error}
            />
            <UserDetailModal
                open={modalOpen}
                onClose={handleModalClose}
                userId={selectedUserId}
            />
        </>
    );

}
